import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { isCrossProductClaimPending } from '@/feature/account-details-sweeps/hooks/use-cross-product-claim-flow';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import {
    hasTermsAndConditionsSupport,
    useTermsAndConditionsModal,
} from '@/feature/terms-and-conditions/components/TermsAndConditionModalProvider';
import { useHasUserAcceptedProductTerms } from '@/feature/terms-and-conditions/hooks/use-has-user-accepted-product-terms';
import { polymarketKycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useResumeEffect } from '@/hooks/use-resume';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { useWalletStore } from '@/hooks/use-wallet';
import { read, save } from '@/utils/async-storage';

import { DID_PROMPT_BIOMETRICS_ENABLE } from './use-prompt-biometric-guard';

const productNameKeys = {
    [Product.Pickem]: 'common:picks',
    [Product.Sweeps]: 'common:sweeps',
} as const;

const DID_PROMPT_FIRST_DEPOSIT = 'didPromptFirstDeposit';

/**
 * Hook used to show "Make your first Deposit" modal the first time
 * It waits for the `didPromptBiometricsEnable` flag to be set
 */
export const useMakeFirstDeposit = () => {
    const {
        wallet,
        walletLoading,
        // balance for current product
        balance,
        actions: {},
    } = useWalletStore();
    const activeProduct = useJurisdictionStore(state => state.product);
    const { t } = useTranslation(['transactions', 'wallet', 'common']);
    const tSocialWallet = useSocialWalletTranslation('transactions');
    const { showInfoSheet } = useAlerts();
    const { dismissAll } = useModals();
    const { isAccepted: hasAcceptedProductTerms } = useHasUserAcceptedProductTerms();
    const { data: isPolymarketKycVerified } = useAuthUserConfig({ select: polymarketKycVerifiedSelector });
    const isPredictions = useIsPredictionsProduct();
    const hasSocialWallet = useHasSocialWallet();

    const { navigate } = useNavigation();
    const { showTermsAndConditionsModal } = useTermsAndConditionsModal();

    const handleFirstDepositModal = useCallback(() => {
        const checkFirstDeposit = async () => {
            const didPromptBiometricsEnable = await read(DID_PROMPT_BIOMETRICS_ENABLE);
            // flag for the modal per product
            const didPromptFirstDeposit = await read(`${DID_PROMPT_FIRST_DEPOSIT}-${activeProduct}`);

            if (
                // make sure we have the wallet loaded
                walletLoading ||
                !wallet ||
                // make sure we have active product
                activeProduct === Product.None ||
                // for Predictions, hold the prompt until the Polymarket KYC has passed
                (isPredictions && isPolymarketKycVerified !== true) ||
                // show the modal only after the prompt for biometric guard
                !didPromptBiometricsEnable ||
                // show the modal only once
                didPromptFirstDeposit ||
                // show the modal only if the balance is 0 (this should be the first deposit)
                balance > 0 ||
                isCrossProductClaimPending()
            ) {
                return;
            }

            // For arcade, we want to show the deposit button label as "Deposit"
            const buttonLabel =
                activeProduct === Product.Pickem
                    ? t('deposit_to_product', { product: t(productNameKeys[activeProduct]) })
                    : hasSocialWallet
                    ? t('wallet:purchase')
                    : t('wallet:deposit');

            showInfoSheet({
                title: tSocialWallet('first_deposit_modal.title'),
                description: tSocialWallet('first_deposit_modal.description'),
                buttonLabel,
                secondaryLabel: t('first_deposit_modal.do_it_later'),
                handlePress: () => {
                    dismissAll();

                    if (hasTermsAndConditionsSupport(activeProduct) && !hasAcceptedProductTerms) {
                        // Delay showing terms modal to ensure first deposit modal is fully dismissed
                        setTimeout(() => {
                            showTermsAndConditionsModal({
                                onSuccess: () => {
                                    if (hasSocialWallet) {
                                        navigate('DepositStack', { screen: 'CoinPackageScreen' });
                                        return;
                                    }

                                    navigate('DepositStack', {
                                        screen: 'DepositScreen',
                                        params: {
                                            shouldRedirectToLobby: true,
                                            disableTransition: true,
                                        },
                                    });
                                },
                            });
                        }, 100);
                    } else {
                        // For products without T&C capabilities set up or when terms already accepted, navigate directly
                        if (hasSocialWallet) {
                            navigate('DepositStack', { screen: 'CoinPackageScreen' });
                            return;
                        }
                        navigate('DepositStack', {
                            screen: 'DepositScreen',
                            params: {
                                shouldRedirectToLobby: true,
                                disableTransition: true,
                            },
                        });
                    }
                },
            });

            save(`${DID_PROMPT_FIRST_DEPOSIT}-${activeProduct}`, 'true');
        };

        // delay execution so that the modal animation doesn't seem that abrupt
        const timeout = setTimeout(() => {
            checkFirstDeposit();
        }, 100);

        return () => clearInterval(timeout);
    }, [
        activeProduct,
        balance,
        dismissAll,
        hasAcceptedProductTerms,
        isPolymarketKycVerified,
        isPredictions,
        hasSocialWallet,
        navigate,
        showInfoSheet,
        showTermsAndConditionsModal,
        t,
        tSocialWallet,
        wallet,
        walletLoading,
    ]);

    useResumeEffect(handleFirstDepositModal);
};
